import React, { FunctionComponent } from 'react';
import { graphql, Link, useStaticQuery } from 'gatsby';
import { RecipePreview } from '../../types/recipePreview';
import { PagePath, SanityArticleCategory, SanityRecipesRelated } from '../../types/common';
import RecipeListCard from './RecipeListCard';
import { InternalLinkIcon } from '../Icons';
import { formatLink } from '../../utils/formatLink';
import './styles.scss';

type RecipesListProps = {
  title?: string | null;
  description?: string | null;
  cta?: string | null;
  recipes?: SanityRecipesRelated | null;
  categories?: SanityArticleCategory[] | null;
  currentRecipeId?: string;
}

type QueryData = {
  allSanityRecipes: {
    nodes: RecipePreview[]
  };
  allSanityRecipeLandingPage: {
    nodes: PagePath[];
  };
}

type getRecipeListProps = {
  allRecipes: RecipePreview[],
  recipesRelated?: SanityRecipesRelated | null,
  categories?: SanityArticleCategory[] | null,
}

function getRecipesList({ allRecipes, recipesRelated, categories }: getRecipeListProps): RecipePreview[] {
  if (recipesRelated && recipesRelated.typeList === 'select' && recipesRelated.selectRecipesRelated) {
    return recipesRelated.selectRecipesRelated;
  }

  if (recipesRelated && recipesRelated.typeList === 'category' && recipesRelated.categoryRecipesRelated) {
    const categoriesIds = recipesRelated.categoryRecipesRelated.map(category => category._id);

    const filteredRecipes = allRecipes.filter(recipe => {
      const recipeCategories = recipe.categories?.map(category => category._id);
      return recipeCategories?.some(item => categoriesIds.includes(item))
    });

    return filteredRecipes;
  }

  if (recipesRelated && recipesRelated.typeList === 'tag' && recipesRelated.tagRecipesRelated) {
    const tagsIds = recipesRelated.tagRecipesRelated.map(tag => tag._id);

    const filteredRecipes = allRecipes.filter(recipe => {
      const recipeTags = recipe.tags?.map(tag => tag._id);
      return recipeTags?.some(item => tagsIds.includes(item))
    });

    return filteredRecipes;
  }

  if (categories) {
    const categoriesIds = categories.map(category => category._id);

    const filteredRecipes = allRecipes.filter(recipe => {
      const recipeCategories = recipe.categories?.map(category => category._id);
      return recipeCategories?.some(item => categoriesIds.includes(item))
    });

    return filteredRecipes;
  }

  return allRecipes;
}

const RecipesList: FunctionComponent<RecipesListProps> = ({ title, description, cta, recipes, categories, currentRecipeId }) => {
  const query = (useStaticQuery<QueryData>(graphql`
    {
      allSanityRecipes(sort: {_createdAt: DESC}) {
        ...RecipesPreviewList
      }
      allSanityRecipeLandingPage {
        nodes {
          _id
          slug {
            current
          }
        }
      }
    }
  `));

  const allRecipes = query.allSanityRecipes.nodes;
  const recipePage = query.allSanityRecipeLandingPage.nodes[0];
  const recipesList = getRecipesList({ allRecipes, recipesRelated: recipes, categories })
    .filter(recipe => currentRecipeId ? currentRecipeId !== recipe._id : true)
    .slice(0, 4);

  return (
    <section className='recipes-list wood'>
      <div>
        <div className='recipes-list-header'>
          <div>
            <h2>{title ? title : 'Recipes'}</h2>
            {description && (
              <p>{description}</p>
            )}
          </div>
          <Link to={formatLink(recipePage)}>
            <span>{cta ? cta : 'Discover more recipes'}</span>
            <InternalLinkIcon />
          </Link>
        </div>

        <div className='recipes-list-container'>
          <div>
            {recipesList.map(recipe => (
              <RecipeListCard
                key={recipe._id}
                recipe={recipe}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default RecipesList;
